import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useLocation, useNavigate } from 'react-router-dom';

import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import DSLoading from '../../../components/designSystem/DSLoading';
import DSModal from '../../../components/designSystem/DSModal';
import RichTextEditor from '../../../components/common/RichTextEditor';
import SafeHtmlViewer from '../../../components/common/SafeHtmlViewer';
import { uploadProjectImage } from '../../../Module/Project/Presentation/projectActions';
import { convertToVwMob } from '../../../Utils/responsive';
import { PATHS } from '../../../routes/paths';
import ProjectDetailHeader from '../../../components/common/ProjectDetailHeader';
import infoIconImg from '../../../assets/Icon/project/info_icon.png';
import searchIcon from '../../../assets/Icon/project/setting/search_icon.png';
import searchActiveIcon from '../../../assets/Icon/project/setting/search_active_icon.png';
import penIcon from '../../../assets/Icon/project/setting/pen_icon.png';
import penActiveIcon from '../../../assets/Icon/project/setting/pen_active_icon.png';
import loadingLottie from '../../../assets/Icon/lottie/Insider-loading.lottie';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { theme } from '../../../styles/theme';
import { getProject, updateProject } from '../../../Module/Project/Presentation/projectActions';
import { selectGetProjectStatus, selectUpdateProjectStatus, selectSelectedProject } from '../../../Module/Project/Presentation/projectSelectors';
import { useTranslation } from 'react-i18next';
import { formatCurrencyInput, formatKoreanCurrency, parseCurrencyNumber } from '../../../Utils/formatCurrency';
import { sanitizeProjectDescHtml } from '../../../Utils/projectDesc';
import projectActiveIcon from '../../../assets/Icon/sidbar/project_active_icon.png';
import postIcon from '../../../assets/Icon/sidbar/post_icon.png';
import sponsorIcon from '../../../assets/Icon/sidbar/sponsor_icon.png';
import payIcon from '../../../assets/Icon/sidbar/pay_icon.png';

const stripEllipsis = (value: string) => value.replace(/\.+$/, '');

type TabKey = 'basic' | 'story';

const sanitizeProjectTitle = (value: string) => {
  return value.replace(/<br\s*\/?>/gi, ' ').replace(/\r?\n/g, ' ');
};

const isValidHttpUrl = (raw: string) => {
  if (!raw.trim()) return false;
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const ProjectSettingsMob = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation');
  const processingLabel = stripEllipsis(t('common.actions.processing'));

  const getStatus = useAppSelector(selectGetProjectStatus);
  const updateStatus = useAppSelector(selectUpdateProjectStatus);
  const project = useAppSelector(selectSelectedProject);

  const projectId = (location.state as { projectId?: string } | undefined)?.projectId;

  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [currency, setCurrency] = useState<'KRW' | 'USD'>('KRW');
  const [goalAmountKrw, setGoalAmountKrw] = useState<string>('');
  const [goalAmountUsd, setGoalAmountUsd] = useState<string>('');
  const goalAmount = currency === 'KRW' ? goalAmountKrw : goalAmountUsd;
  const setGoalAmount = currency === 'KRW' ? setGoalAmountKrw : setGoalAmountUsd;
  const [title, setTitle] = useState<string>('');
  const [projectDesc, setProjectDesc] = useState<string>('');
  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string>('');

  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [exitConfirmTarget, setExitConfirmTarget] = useState<'media' | 'story' | null>(null);
  const [tempMediaType, setTempMediaType] = useState<'image' | 'video' | 'none'>('image');
  const [tempMediaFile, setTempMediaFile] = useState<File | null>(null);
  const [tempMediaPreview, setTempMediaPreview] = useState<string | null>(null);
  const [tempVideoUrl, setTempVideoUrl] = useState<string>('');
  const [tempVideoUrlFocused, setTempVideoUrlFocused] = useState(false);
  const [tempVideoPreviewEnabled, setTempVideoPreviewEnabled] = useState(false);
  const [tempProjectDesc, setTempProjectDesc] = useState<string>('');
  const mediaInputRef = useRef<HTMLInputElement | null>(null);
  const titleTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const adjustTitleTextareaHeight = useCallback(() => {
    const el = titleTextareaRef.current;
    if (!el) return;

    const computed = window.getComputedStyle(el);
    const lineHeight = Number.parseFloat(computed.lineHeight || '0') || 0;
    const maxHeight = lineHeight > 0 ? lineHeight * 2 : 0;

    el.style.height = 'auto';
    if (maxHeight > 0) {
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
      return;
    }

    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = 'hidden';
  }, []);

  const openFilePicker = (input: HTMLInputElement | null) => {
    if (!input) return;
    const picker = (input as HTMLInputElement & { showPicker?: () => void }).showPicker;
    if (typeof picker === 'function') {
      picker.call(input);
      return;
    }
    input.click();
  };

  const isLoading = getStatus === 'loading' && (!project || project.project_id !== projectId);
  const isSaving = updateStatus === 'loading';

  const titlePreview = useMemo(() => {
    const trimmedTitle = sanitizeProjectTitle(project?.title ?? title ?? '').trim();
    return trimmedTitle ? trimmedTitle.slice(0, 40) : t('page.projectSettings.placeholders.projectTitlePreviewMobile');
  }, [project?.title, title, t]);

  useEffect(() => {
    if (!projectId) return;
    // 이미 로딩 중이거나 현재 프로젝트가 요청하려는 프로젝트와 같으면 중단
    if (getStatus === 'loading' || project?.project_id === projectId) return;
    dispatch(getProject({ project_id: projectId }));
  }, [dispatch, projectId, getStatus, project?.project_id]);

  useEffect(() => {
    if (!project) return;
    setCurrency((project.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW');
    const projectCurrency = (project.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW';
    const formatted = project.goal_amount ? formatGoal(String(project.goal_amount), projectCurrency) : '';
    setGoalAmountKrw(prev => (projectCurrency === 'KRW' ? formatted : prev));
    setGoalAmountUsd(prev => (projectCurrency === 'USD' ? formatted : prev));
    setTitle(sanitizeProjectTitle(project.title ?? ''));
    setProjectDesc(String(project.project_desc ?? ''));
    setMediaPreview(project.image_url ? String(project.image_url) : null);
    setVideoUrl(project.video_url ? String(project.video_url) : '');
  }, [project]);

  useEffect(() => {
    adjustTitleTextareaHeight();
  }, [title, activeTab, adjustTitleTextareaHeight]);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const readUrlAsBase64 = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return readFileAsBase64(new File([blob], 'image.png', { type: blob.type || 'image/png' }));
  };

  const formatGoal = (raw: string, targetCurrency: 'KRW' | 'USD') => {
    return formatCurrencyInput(raw, targetCurrency);
  };

  const handleCancel = () => {
    const projectCurrency = (project?.currency_type as 'KRW' | 'USD' | undefined) ?? 'KRW';
    setCurrency(projectCurrency);
    const formatted = project?.goal_amount ? formatGoal(String(project.goal_amount), projectCurrency) : '';
    setGoalAmountKrw(projectCurrency === 'KRW' ? formatted : '');
    setGoalAmountUsd(projectCurrency === 'USD' ? formatted : '');
    setTitle(sanitizeProjectTitle(project?.title ?? ''));
    setProjectDesc(String(project?.project_desc ?? ''));
    setMediaPreview(project?.image_url ? String(project.image_url) : null);
    setVideoUrl(project?.video_url ? String(project.video_url) : '');
    setMediaFile(null);
  };

  const handleSave = async (overrideData?: { mediaFile?: File | null; projectDesc?: string; videoUrl?: string; mediaPreview?: string | null }) => {
    if (!projectId) return;
    const partner = String(project?.partner ?? (process.env.REACT_APP_PARTNER as string | undefined) ?? 'OMF');
    const numeric = parseCurrencyNumber(currency === 'KRW' ? goalAmountKrw : goalAmountUsd);

    try {
      let imageBase64: string | undefined;

      // overrideData에 mediaFile이 명시적으로 null이면 삭제를 의미
      const targetMediaFile = overrideData && 'mediaFile' in overrideData ? overrideData.mediaFile : mediaFile;
      const targetMediaPreview = overrideData && 'mediaPreview' in overrideData ? overrideData.mediaPreview : mediaPreview;

      if (targetMediaFile) {
        imageBase64 = await readFileAsBase64(targetMediaFile);
      } else {
        imageBase64 = '';
      }

      let updatedProjectDesc = overrideData?.projectDesc ?? projectDesc;
      const overrideVideoUrl = overrideData?.videoUrl;

      // Handle blob URLs in projectDesc
      const blobUrls = updatedProjectDesc.match(/src="blob:[^"]+"/g) || [];
      for (const match of blobUrls) {
        const url = match.slice(5, -1);
        try {
          const resBlob = await fetch(url);
          const blob = await resBlob.blob();
          const dataUrl = await readFileAsBase64(new File([blob], 'image.png', { type: blob.type }));
          const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;

          const uploadRes = await dispatch(
            uploadProjectImage({
              partner,
              project_id: projectId,
              projectpost_id: '',
              language_code: 'ko',
              isuse: true,
              image: base64,
            }),
          ).unwrap();

          if (uploadRes.image_url) {
            updatedProjectDesc = updatedProjectDesc.replace(url, uploadRes.image_url);
          }
        } catch (uploadErr) {
          console.error('Embedded image upload failed:', uploadErr);
        }
      }

      updatedProjectDesc = sanitizeProjectDescHtml(updatedProjectDesc);

      const updatePayload: any = {
        project_id: projectId,
        partner,
        title: sanitizeProjectTitle(title).trim().slice(0, 40),
        goal_amount: numeric ? numeric : undefined,
        currency_type: currency,
        project_desc: updatedProjectDesc,
        is_image_display: typeof project?.is_image_display === 'string' ? project.is_image_display : undefined,
        video_url: overrideVideoUrl ?? project?.video_url,
        user_id: project?.user_id,
        user_full_name: project?.user_full_name,
        type: project?.type,
        start_date: project?.start_date,
        end_date: project?.end_date,
        status: project?.status,
      };

      updatePayload.image = imageBase64;
      if (!imageBase64 && targetMediaPreview) {
        updatePayload.image_url = targetMediaPreview;
      }

      await dispatch(updateProject(updatePayload)).unwrap();
      // eslint-disable-next-line no-alert
      alert(t('common.messages.saved'));
    } catch (e: any) {
      // eslint-disable-next-line no-alert
      alert(e?.message || (typeof e === 'string' ? e : t('page.projectSettings.errors.saveFailed')));
    }
  };

  const handleOpenMediaModal = () => {
    const nextMediaType = project?.video_url ? 'video' : project?.image_url ? 'image' : 'none';
    setTempMediaType(nextMediaType);
    setTempMediaPreview(mediaPreview);
    setTempMediaFile(null);
    setTempVideoUrl(project?.video_url ? String(project.video_url) : '');
    setTempVideoPreviewEnabled(Boolean(project?.video_url));
    setIsMediaModalOpen(true);
  };

  const handleSaveMediaModal = async () => {
    if (tempMediaType === 'none') {
      setMediaPreview(null);
      setMediaFile(null);
      setVideoUrl('');
      await handleSave({ mediaFile: null, videoUrl: '', mediaPreview: null });
    } else if (tempMediaType === 'video') {
      const trimmed = tempVideoUrl.trim();
      const nextVideoUrl = isValidHttpUrl(trimmed) ? trimmed : '';
      setMediaPreview(null);
      setMediaFile(null);
      setVideoUrl(nextVideoUrl);
      await handleSave({ mediaFile: null, videoUrl: nextVideoUrl, mediaPreview: null });
    } else {
      if (tempMediaFile) {
        setMediaFile(tempMediaFile);
        setMediaPreview(tempMediaPreview);
      }
      setVideoUrl('');
      await handleSave({ mediaFile: tempMediaFile, videoUrl: '' });
    }
    setIsMediaModalOpen(false);
  };

  const handleOpenStoryModal = () => {
    setTempProjectDesc(projectDesc);
    setIsStoryModalOpen(true);
  };

  const handleSaveStoryModal = async () => {
    setProjectDesc(tempProjectDesc);
    await handleSave({ projectDesc: tempProjectDesc });
    setIsStoryModalOpen(false);
  };

  const fundingLabel = project?.type === 'OT'
    ? t('page.projectManagement.fundingType.oneTime')
    : t('page.projectManagement.fundingType.recurring');
  const createdText = project?.created_at ? String(project.created_at).slice(0, 10).replaceAll('-', '.') : 'YYYY.MM.DD';
  const keymanText = project?.keyman ? t('common.labels.present') : t('common.labels.none');
  const supportPercent =
    typeof (project?.support_project as any)?.support_percent === 'string'
      ? (project?.support_project as any).support_percent
      : '3';
  const supportText = t('page.projectSettings.labels.supporting', { percent: supportPercent });


  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setTempMediaFile(file);
      setTempMediaPreview(URL.createObjectURL(file));
    }
  };

  const buildVideoEmbedUrl = (rawUrl: string) => {
    if (!rawUrl.trim()) return '';
    try {
      const url = new URL(rawUrl.trim());
      const host = url.hostname.replace('www.', '');
      if (host === 'youtu.be') {
        const id = url.pathname.slice(1);
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      if (host.includes('youtube.com')) {
        if (url.pathname.startsWith('/embed/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        if (url.pathname.startsWith('/shorts/')) {
          const id = url.pathname.split('/')[2];
          return id ? `https://www.youtube.com/embed/${id}` : '';
        }
        const id = url.searchParams.get('v');
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }
      return '';
    } catch {
      return '';
    }
  };

  const videoEmbedUrl = useMemo(() => buildVideoEmbedUrl(videoUrl), [videoUrl]);
  const videoPreviewSrc = useMemo(() => {
    const trimmed = videoUrl.trim();
    if (!isValidHttpUrl(trimmed)) return '';
    return videoEmbedUrl || trimmed;
  }, [videoEmbedUrl, videoUrl]);
  const tempVideoEmbedUrl = useMemo(() => buildVideoEmbedUrl(tempVideoUrl), [tempVideoUrl]);
  const tempVideoPreviewSrc = useMemo(() => {
    const trimmed = tempVideoUrl.trim();
    if (!isValidHttpUrl(trimmed)) return '';
    return tempVideoEmbedUrl || trimmed;
  }, [tempVideoEmbedUrl, tempVideoUrl]);
  const showTempVideoUrlError = Boolean(tempVideoUrl.trim()) && !isValidHttpUrl(tempVideoUrl.trim());
  const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const showGoalPreview = currency === 'KRW' && numericGoal >= 10000;
  const goalPreviewText = showGoalPreview ? formatKoreanCurrency(numericGoal) : '';

  const handleRequestCloseModal = (target: 'media' | 'story') => {
    setExitConfirmTarget(target);
  };

  const handleConfirmExit = () => {
    if (exitConfirmTarget === 'media') setIsMediaModalOpen(false);
    if (exitConfirmTarget === 'story') setIsStoryModalOpen(false);
    setExitConfirmTarget(null);
  };

  const handleCurrencyChange = (next: 'KRW' | 'USD') => {
    if (next === currency) return;
    setCurrency(next);
    setGoalAmountKrw('');
    setGoalAmountUsd('');
  };

  if (isLoading) {
    return (
      <MobilePageLayout title={t('page.projectSettings.pageTitle')}>
        <DSLoading />
      </MobilePageLayout>
    );
  }

  return (
    <MobilePageLayout title={t('page.projectSettings.pageTitle')}>
      <Page>
        <ProjectDetailHeader title={titlePreview} projectId={projectId} variant="mobile" />

        <PageTitle>{t('page.projectSettings.pageTitle')}</PageTitle>

        <TabsBox>
          <TabButton type="button" $active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>
            <TabIcon src={activeTab === 'basic' ? searchActiveIcon : searchIcon} alt="" />
            {t('page.projectSettings.tabs.basic')}
          </TabButton>
          <TabButton type="button" $active={activeTab === 'story'} onClick={() => setActiveTab('story')}>
            <TabIcon src={activeTab === 'story' ? penActiveIcon : penIcon} alt="" />
            {t('page.projectSettings.tabs.story')}
          </TabButton>
        </TabsBox>

        {activeTab === 'basic' ? (
          <>
            <PanelCard style={{ background: '#F9FAFB', border : 'none', paddingTop : '8px', paddingLeft : '6px' , paddingRight: '6px'}}>
              <FieldTitle>{t('page.projectSettings.sections.goalAmount')}</FieldTitle>

              <CurrencyRow>
                <CurrencyBtn type="button" $active={currency === 'KRW'} onClick={() => handleCurrencyChange('KRW')}>
                  <RadioDot $active={currency === 'KRW'} /> {t('common.currency.krw')}
                </CurrencyBtn>
                <CurrencyBtn type="button" $active={currency === 'USD'} onClick={() => handleCurrencyChange('USD')}>
                  <RadioDot $active={currency === 'USD'} /> {t('common.currency.usd')}
                </CurrencyBtn>
              </CurrencyRow>

              <InputBox>
                <InputLabel>{t('page.projectSettings.labels.goalAmount')}</InputLabel>
                <InputField
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(formatGoal(e.target.value, currency))}
                  placeholder={t('page.projectSettings.placeholders.goalAmount')}
                  inputMode="numeric"
                />
              </InputBox>

              {showGoalPreview ? <GoalPreview>{goalPreviewText}</GoalPreview> : null}

              <HelperText>{currency === 'KRW' ? t('page.projectSettings.helpers.minGoal') : t('page.projectSettings.helpers.minGoalUSD')}</HelperText>
              <WarnRow>
                <WarnIcon src={infoIconImg} alt="info" />
                <WarnText>
                  {(t('page.projectSettings.helpers.recurringGoal') as string).split('\n').map((line, idx) => (
                    <React.Fragment key={`recurring-goal-${idx}`}>
                      {line}
                      {idx === 0 && <br />}
                    </React.Fragment>
                  ))}
                </WarnText>
              </WarnRow>

              <FieldTitle style={{marginTop : '18px'}}>{t('page.projectSettings.sections.projectTitle')}</FieldTitle>
              <TitleInputBox>
                <InputLabel>{t('page.projectSettings.labels.projectTitle')}</InputLabel>
                <InputTextArea
                  ref={titleTextareaRef}
                  value={title}
                  onChange={(e) => {
                    setTitle(sanitizeProjectTitle(e.target.value).slice(0, 40));
                    setTimeout(adjustTitleTextareaHeight, 0);
                  }}
                  onFocus={adjustTitleTextareaHeight}
                  placeholder={t('page.projectSettings.placeholders.projectTitleHint')}
                  rows={2}
                />
              </TitleInputBox>
              <HelperText>{t('page.projectSettings.helpers.titleLengthShort')}</HelperText>

            <ButtonRow>
              <ActionButton type="button" $variant="cancel" onClick={handleCancel} disabled={isSaving}>
                {t('common.actions.cancel')}
              </ActionButton>
              <ActionButton
                type="button"
                $variant="save"
                $loading={isSaving}
                onClick={() => handleSave()}
                disabled={isSaving || !projectId}
                aria-busy={isSaving}
                aria-label={isSaving ? processingLabel : undefined}
              >
                {isSaving ? (
                  <LoadingIcon
                    src={loadingLottie}
                    autoplay
                    loop
                    aria-hidden
                    style={{ width: 12, height: 12 }}
                  />
                ) : t('common.actions.save')}
              </ActionButton>
            </ButtonRow>
            </PanelCard>

            <PanelCard>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.fundingType')}</InfoLabel>
                <InfoValue>{fundingLabel}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.createdAt')}</InfoLabel>
                <InfoValue>{createdText}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.keyman')}</InfoLabel>
                <InfoValue>{keymanText}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>{t('page.projectSettings.info.missionFund')}</InfoLabel>
                <InfoValue>{supportText}</InfoValue>
              </InfoRow>
            </PanelCard>
          </>
        ) : (
          <>
            <PanelCard>
              <CardHeaderRow>
                <CardTitle>{t('page.projectSettings.sections.media')}</CardTitle>
                <EditButton type="button" onClick={handleOpenMediaModal}>
                  {t('page.projectSettings.actions.edit')}
                </EditButton>
              </CardHeaderRow>
              <MediaBox $hasImage={!videoPreviewSrc && Boolean(mediaPreview)}>
                {videoPreviewSrc ? (
                  <MediaIframe
                    src={videoPreviewSrc}
                    title="video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : mediaPreview ? (
                  <MediaImg src={mediaPreview} alt="" />
                ) : (
                  <EmptyText>{t('page.postManagement.modal.mediaType.none')}</EmptyText>
                )}
                {(videoPreviewSrc || mediaPreview) ? <Badge>{t('common.labels.main')}</Badge> : null}
              </MediaBox>
            </PanelCard>

            <PanelCard>
              <CardHeaderRow>
                <CardTitle>{t('page.projectSettings.sections.story')}</CardTitle>
                <EditButton type="button" onClick={handleOpenStoryModal}>
                  {t('page.projectSettings.actions.edit')}
                </EditButton>
              </CardHeaderRow>
              <StoryBox>
                {projectDesc ? (
                  <StoryHtmlViewer html={projectDesc} />
                ) : (
                  <EmptyText>{t('page.projectSettings.empty.story')}</EmptyText>
                )}
              </StoryBox>
            </PanelCard>
          </>
        )}

        <BottomInset />
      </Page>

      <BottomTabBar>
        <BottomTab type="button" onClick={() => navigate(PATHS.POST_MANAGEMENT, { state: { projectId } })}>
          <BottomTabIcon src={postIcon} alt="" />
          {t('page.postManagement.pageTitle')}
        </BottomTab>
        <BottomTab type="button" disabled>
          <BottomTabIcon src={sponsorIcon} alt="" />
          {t('page.donorManagement.pageTitle')}
        </BottomTab>
        <BottomTab type="button" disabled>
          <BottomTabIcon src={payIcon} alt="" />
          {t('page.paymentStatus.pageTitle')}
        </BottomTab>
        <BottomTab type="button" $active>
          <BottomTabIcon src={projectActiveIcon} alt="" />
          {t('page.projectManagement.manage.settings')}
        </BottomTab>
      </BottomTabBar>

      <DSModal
        open={Boolean(exitConfirmTarget)}
        size="compact"
        title={t('common.exitConfirm.title')}
        description={t('common.exitConfirm.description')}
        onClose={() => setExitConfirmTarget(null)}
        actions={[
          { label: t('common.exitConfirm.leave'), variant: 'secondary', onClick: handleConfirmExit },
          { label: t('common.exitConfirm.continue'), variant: 'primary', onClick: () => setExitConfirmTarget(null) },
        ]}
      />

      {/* Media Edit Modal */}
      {isMediaModalOpen && (
        <ModalOverlay onClick={() => setIsMediaModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('page.projectSettings.modal.editTitle')}</ModalTitle>
              <CloseBtn onClick={() => handleRequestCloseModal('media')}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <SectionDesc>{t('page.postManagement.modal.mediaSectionTitle')}</SectionDesc>
              <RadioRow>
                <RadioItem>
                  <RadioInput
                    type="radio"
                    checked={tempMediaType === 'image'}
                    onChange={() => setTempMediaType('image')}
                  />
                  {t('page.postManagement.modal.mediaType.image')}
                </RadioItem>
                <RadioItem>
                  <RadioInput
                    type="radio"
                    checked={tempMediaType === 'video'}
                    onChange={() => setTempMediaType('video')}
                  />
                  {t('page.postManagement.modal.mediaType.video')}
                </RadioItem>
              </RadioRow>

              {tempMediaType === 'image' && (
                <FileRow>
                  <FileNameBox>{tempMediaFile?.name || t('page.postManagement.modal.uploadHintImage')}</FileNameBox>
                  <AttachBtn type="button" onClick={() => openFilePicker(mediaInputRef.current)}>{t('common.actions.attach')}</AttachBtn>
                  <input
                    id="modal-media-upload-mob"
                    type="file"
                    accept="image/*"
                    style={{
                      position: 'absolute',
                      width: 1,
                      height: 1,
                      padding: 0,
                      margin: -1,
                      overflow: 'hidden',
                      clip: 'rect(0, 0, 0, 0)',
                      whiteSpace: 'nowrap',
                      border: 0,
                    }}
                    onChange={handleMediaChange}
                    ref={mediaInputRef}
                  />
                </FileRow>
              )}

              {tempMediaType === 'video' && (
                <>
                  <VideoUrlRow>
                    <VideoUrlField
                      $state={showTempVideoUrlError ? 'error' : tempVideoUrlFocused ? 'focus' : tempVideoUrl.trim() ? 'complete' : 'default'}
                    >
                      <VideoUrlText>
                        {(tempVideoUrlFocused || tempVideoUrl.trim()) && <VideoUrlLabel>{t('page.projectSettings.media.youtubeLabel')}</VideoUrlLabel>}
                        <VideoUrlInput
                          placeholder={tempVideoUrlFocused || tempVideoUrl.trim() ? '' : t('page.projectSettings.media.youtubePlaceholder')}
                          value={tempVideoUrl}
                          onChange={(e) => {
                            setTempVideoUrl(e.target.value);
                            setTempVideoPreviewEnabled(true);
                          }}
                          onFocus={() => setTempVideoUrlFocused(true)}
                          onBlur={() => setTempVideoUrlFocused(false)}
                        />
                      </VideoUrlText>
                      {tempVideoUrl.trim() ? (
                        <VideoUrlClear
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setTempVideoUrl('');
                            setTempVideoPreviewEnabled(false);
                          }}
                          aria-label="clear"
                        >
                          ×
                        </VideoUrlClear>
                      ) : null}
                    </VideoUrlField>
                  </VideoUrlRow>
                  {showTempVideoUrlError ? <VideoUrlError>{t('page.projectSettings.media.invalidUrl')}</VideoUrlError> : null}
                </>
              )}

              <PreviewArea $hasImage={tempMediaType === 'image' && Boolean(tempMediaPreview)}>
                {tempMediaType === 'none' ? (
                  <EmptyText>{t('page.projectSettings.media.empty')}</EmptyText>
                ) : tempMediaType === 'video' && tempVideoPreviewEnabled && tempVideoPreviewSrc ? (
                  <PreviewIframe
                    src={tempVideoPreviewSrc}
                    title="video preview"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : tempMediaType === 'video' ? (
                  <EmptyText>{t('page.projectSettings.media.linkPreviewHint').replace(' 미리보기가 ', '\n미리보기가 ')}</EmptyText>
                ) : tempMediaPreview ? (
                  <>
                    <PreviewImg src={tempMediaPreview} alt="" />
                    <Badge>{t('page.postManagement.modal.mainBadge')}</Badge>
                  </>
                ) : (
                  <EmptyText>{t('page.postManagement.modal.previewHintImage').replace(' 미리보기가 ', '\n미리보기가 ')}</EmptyText>
                )}
              </PreviewArea>
            </ModalBody>
            <ModalFooter>
              <ModalActionBtn $variant="cancel" onClick={() => setIsMediaModalOpen(false)} disabled={isSaving}>
                {t('common.actions.cancel')}
              </ModalActionBtn>
              <ModalActionBtn
                $variant="save"
                $loading={isSaving}
                onClick={handleSaveMediaModal}
                disabled={isSaving}
                aria-busy={isSaving}
                aria-label={isSaving ? processingLabel : undefined}
              >
                {isSaving ? (
                  <LoadingIcon
                    src={loadingLottie}
                    autoplay
                    loop
                    aria-hidden
                    style={{ width: 12, height: 12 }}
                  />
                ) : t('common.actions.save')}
              </ModalActionBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Story Edit Modal */}
      {isStoryModalOpen && (
        <ModalOverlay onClick={() => setIsStoryModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{t('page.projectSettings.modal.editTitle')}</ModalTitle>
              <CloseBtn onClick={() => handleRequestCloseModal('story')}>✕</CloseBtn>
            </ModalHeader>
            <ModalBody>
              <SectionDesc>
                {t('page.projectSettings.modal.storyDescLine1')}<br />
                {t('page.projectSettings.modal.storyDescLine2')}
              </SectionDesc>
              <RichTextEditor
                value={tempProjectDesc}
                onChange={(html) => setTempProjectDesc(html)}
                placeholder={t('page.postManagement.modal.storyPlaceholder')}
                pictureIconSrc="/Image/posts/photoIcon.png"
                maxImages={3}
                imageCount={(tempProjectDesc.match(/<figure/g) || []).length}
                showToolbar={true}
              />
            </ModalBody>
            <ModalFooter>
              <ModalActionBtn $variant="cancel" onClick={() => setIsStoryModalOpen(false)} disabled={isSaving}>{t('common.actions.cancel')}</ModalActionBtn>
              <ModalActionBtn
                $variant="save"
                $loading={isSaving}
                onClick={handleSaveStoryModal}
                disabled={isSaving}
                aria-busy={isSaving}
                aria-label={isSaving ? processingLabel : undefined}
              >
                {isSaving ? (
                  <LoadingIcon
                    src={loadingLottie}
                    autoplay
                    loop
                    aria-hidden
                    style={{ width: 12, height: 12 }}
                  />
                ) : t('common.actions.save')}
              </ModalActionBtn>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </MobilePageLayout>
  );
};

export default ProjectSettingsMob;

const BottomTabBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${convertToVwMob(84)};
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  align-items: center;
  justify-content: space-around;
  padding-bottom: env(safe-area-inset-bottom);
  z-index: 100;
`;

const BottomTab = styled.button<{ $active?: boolean }>`
  flex: 1;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(4)};
  cursor: pointer;

  color: ${({ $active }) => ($active ? '#2BC2E1' : '#BCBCBC')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const BottomTabIcon = styled.img`
  width: ${convertToVwMob(32)};
  height: ${convertToVwMob(32)};
  object-fit: contain;
`;

const Page = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(14)};
`;

const PageTitle = styled.h2`
  margin: ${convertToVwMob(8)} 0 0;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

const TabsBox = styled.div`
  display: flex;
  border-radius: ${convertToVwMob(10)} ${convertToVwMob(10)} 0 0;
  border: 1px solid #eeeeee;
  background: #ffffff;
  overflow: hidden;
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: ${convertToVwMob(12)} ${convertToVwMob(20)};
  display: inline-flex;
  gap: ${convertToVwMob(10)};
  align-items: center;
  justify-content: flex-start;
  border: none;
  background: transparent;
  cursor: pointer;
  position: relative;

  color: ${({ $active }) => ($active ? '#2BC2E1' : '#BCBCBC')};
  text-align: center;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: ${convertToVwMob(3)};
    border-radius: 0;
    background: ${({ $active }) => ($active ? '#2BC2E1' : 'transparent')};
  }
`;

const TabIcon = styled.img`
  width: ${convertToVwMob(22)};
  height: ${convertToVwMob(22)};
  object-fit: contain;
`;

const PanelCard = styled.div`
  border-radius: ${convertToVwMob(10)};
  border: 1px solid #eeeeee;
  background: #ffffff;
  padding: ${convertToVwMob(18)};
`;

const FieldTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const CurrencyRow = styled.div`
  margin-top: ${convertToVwMob(10)};
  display: flex;
  gap: ${convertToVwMob(18)};
  align-items: center;
`;

const CurrencyBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${convertToVwMob(8)};

  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const RadioDot = styled.span<{ $active: boolean }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  border-radius: 999px;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2bc2e1;
    transform: ${({ $active }) =>
      $active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const InputBox = styled.div`
  border-radius: ${convertToVwMob(4)};
  border: 1px solid #EEEEEE;
  background: #ffffff;
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #2bc2e1;
  }

  &:focus-within {
    border-color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
  }
`;

const TitleInputBox = styled.div`
  margin-top: ${convertToVwMob(8)};
  width: 100%;
  max-width: 353px;
  border-radius: ${convertToVwMob(4)};
  border: 1px solid #eeeeee;
  background: #ffffff;
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: #2bc2e1;
  }

  &:focus-within {
    border-color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
  }
`;

const InputLabel = styled.div`
  color: #676767;
 ${({ theme }) => theme.typography.contents.medium20}
`;

const InputField = styled.input`
  width: 100%;
  border: none;
  outline: none;

  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  background: transparent;
`;

const GoalPreview = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  white-space: normal;
  word-break: keep-all;
`;

const InputTextArea = styled.textarea`
  margin-top: ${convertToVwMob(6)};
  width: 100%;
  border: none;
  outline: none;
  resize: none;

  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  background: transparent;
`;

const Divider = styled.div<{ $fullWidth?: boolean }>`
  margin: ${convertToVwMob(18)} 0;
  height: 1px;
  background: #EEEEEE;
  ${({ $fullWidth }) =>
    $fullWidth &&
    `
    margin-left: -${convertToVwMob(18)};
    margin-right: -${convertToVwMob(18)};
    width: calc(100% + ${convertToVwMob(36)});
  `}
`;

const HelperText = styled.div`
  margin-top: ${convertToVwMob(8)};
  color: #357b89;
  font-size: ${convertToVwMob(16)};
 ${theme.typography.contents.regular16}
`;

const WarnRow = styled.div`
  margin-top: ${convertToVwMob(8)};
  display: flex;
  align-items: flex-start;
  gap: ${convertToVwMob(8)};
`;

const WarnIcon = styled.img`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  margin-top: ${convertToVwMob(10)};
`;

const WarnText = styled.div`
  color: #ff9500;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ButtonRow = styled.div`
  margin-top: ${convertToVwMob(18)};
  display: flex;
  justify-content: flex-end;
  gap: ${convertToVwMob(16)};
`;

const ActionButton = styled.button<{ $variant: 'cancel' | 'save'; $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(4)};
  cursor: pointer;
  white-space: nowrap;
${({ theme }) => theme.typography.button.regular20}
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;

  ${({ $variant }) =>
    $variant === 'cancel'
      ? `
    color: #676767;
    border: 1px solid #DFDFDF;
    background: #FFF;
  `
      : `
    color: #FFF;
    border: none;
    background: #026478;
  `}

  ${({ $variant, $loading }) =>
    $variant === 'save' && $loading
      ? `
    border-radius: ${convertToVwMob(10)};
    background: #EBFCFF;
    color: #2BC2E1;
  `
      : ''}

  opacity: ${({ disabled, $loading }) => (disabled ? ($loading ? 1 : 0.5) : 1)};
`;

const LoadingIcon = styled(DotLottieReact)`
  display: inline-flex;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${convertToVwMob(12)} 0;
`;

const InfoLabel = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const InfoValue = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${convertToVwMob(12)};
  border-bottom: 1px solid #eeeeee;
  margin-left: calc(-1 * ${convertToVwMob(18)});
  margin-right: calc(-1 * ${convertToVwMob(18)});
  padding-left: ${convertToVwMob(18)};
  padding-right: ${convertToVwMob(18)};
`;

const CardTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
`;

const EditButton = styled.button`
  border-radius: ${convertToVwMob(100)};
  border: 1px solid #82e8ff;
  background: #ffffff;
  color: #2bc2e1;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  padding: ${convertToVwMob(8)} ${convertToVwMob(20)};
  cursor: pointer;
`;

const MediaBox = styled.div<{ $hasImage?: boolean }>`
  margin-top: ${convertToVwMob(12)};
  border-radius: ${convertToVwMob(10)};
  border: 2px solid #2bc2e1;
  background: #f0f1f2;
  height: ${({ $hasImage }) => ($hasImage ? 'auto' : convertToVwMob(220))};
  display: ${({ $hasImage }) => ($hasImage ? 'block' : 'flex')};
  align-items: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  justify-content: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  position: relative;
  overflow: hidden;
`;

const MediaImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const Badge = styled.div`
  position: absolute;
  top: ${convertToVwMob(12)};
  left: ${convertToVwMob(12)};
  background: #2bc2e1;
  color: #ffffff;
  border-radius: ${convertToVwMob(6)};
  padding: ${convertToVwMob(6)} ${convertToVwMob(10)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;
`;

const MediaIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const StoryBox = styled.div`
  margin-top: ${convertToVwMob(12)};
  background: #ffffff;
`;

const StoryHtmlViewer = styled(SafeHtmlViewer)`
  color: var(--GreyScale-70, #676767);
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: ${convertToVwMob(20)};
`;

const ModalContent = styled.div`
  width: 100%;
  background: #fff;
  border-radius: ${convertToVwMob(20)};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: ${convertToVwMob(20)} ${convertToVwMob(20)} ${convertToVwMob(14)};
  background: #f8f8f8;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 600;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  font-size: ${convertToVwMob(20)};
  color: #bcbcbc;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: ${convertToVwMob(20)};
  flex: 1;
  overflow-y: auto;
  max-height: 70vh;
`;

const SectionDesc = styled.div`
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  margin-bottom: ${convertToVwMob(20)};
  line-height: 1.4;
`;

const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${convertToVwMob(16)};
  margin-bottom: ${convertToVwMob(24)};
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(6)};
  cursor: pointer;
  color: #17171b;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
`;

const RadioInput = styled.input`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  border-radius: 50%;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2bc2e1;
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.15s ease;
  }

  &:checked::after {
    transform: translate(-50%, -50%) scale(1);
  }
`;

const FileRow = styled.div`
  display: flex;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(16)};
`;

const FileNameBox = styled.div`
  flex: 1;
  height: ${convertToVwMob(44)};
  background: #f8f8f8;
  border-radius: ${convertToVwMob(4)};
  padding: 0 ${convertToVwMob(10)};
  display: flex;
  align-items: center;
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachBtn = styled.button`
  width: ${convertToVwMob(80)};
  height: ${convertToVwMob(44)};
  background: #fff;
  border: 1px solid #bcbcbc;
  border-radius: ${convertToVwMob(4)};
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  cursor: pointer;

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):active {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;

const PreviewArea = styled.div<{ $hasImage?: boolean }>`
  width: 100%;
  height: ${({ $hasImage }) => ($hasImage ? 'auto' : convertToVwMob(180))};
  background: ${({ $hasImage }) => ($hasImage ? 'transparent' : '#f0f0f0')};
  border-radius: 5px;
  display: ${({ $hasImage }) => ($hasImage ? 'block' : 'flex')};
  align-items: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  justify-content: ${({ $hasImage }) => ($hasImage ? 'stretch' : 'center')};
  position: relative;
  overflow: hidden;
  border: 2px solid #2bc2e1;
`;

const PreviewImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ModalFooter = styled.div`
  padding: ${convertToVwMob(16)} ${convertToVwMob(31)} ${convertToVwMob(16)};
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: ${convertToVwMob(16)};
`;

const ModalActionBtn = styled.button<{ $variant: 'cancel' | 'save'; $loading?: boolean }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(4)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(6)};
  white-space: nowrap;

  ${({ $variant }) =>
    $variant === 'cancel'
      ? `
    background: #fff;
    border: 1px solid #DFDFDF;
    color: #676767;
  `
      : `
    background: #026478;
    border: none;
    color: #fff;
  `}

  ${({ $variant, $loading }) =>
    $variant === 'save' && $loading
      ? `
    border-radius: 5px;
    background: #EBFCFF;
    color: #2BC2E1;
  `
      : ''}

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
  }
`;

const VideoUrlRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(16)};
`;

const VideoUrlField = styled.div<{ $state: 'default' | 'focus' | 'complete' | 'error' }>`
  flex: 1;
  border-radius: ${convertToVwMob(6)};
  border: ${convertToVwMob(2)} solid
    ${({ $state }) =>
      $state === 'error' ? '#ff0f0f' : $state === 'focus' ? '#2bc2e1' : '#dfdfdf'};
  background: #ffffff;
  padding: ${convertToVwMob(10)} ${convertToVwMob(12)};
  min-height: ${convertToVwMob(40)};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
`;

const VideoUrlText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
  min-width: 0;
`;

const VideoUrlLabel = styled.div`
  color: #676767;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoUrlInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 700;
  color: #17171b;
  background: transparent;

  &::placeholder {
    color: #bcbcbc;
    font-weight: 400;
  }
`;

const VideoUrlClear = styled.button`
  width: ${convertToVwMob(28)};
  height: ${convertToVwMob(28)};
  border-radius: 999px;
  border: none;
  background: #bcbcbc;
  color: #ffffff;
  font-size: ${convertToVwMob(18)};
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 0 0 auto;
`;

const VideoUrlError = styled.div`
  margin-bottom: ${convertToVwMob(6)};
  color: #ff0f0f;
  font-family: Pretendard;
  font-size: ${convertToVwMob(12)};
  font-weight: 400;
`;

const VideoAttachBtn = styled.button`
  height: ${convertToVwMob(56)};
  width: ${convertToVwMob(96)};
  border-radius: ${convertToVwMob(12)};
  border: 1px solid #dfdfdf;
  background: #ffffff;
  color: #404040;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 500;
  line-height: 150%;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:not(:disabled):hover {
    border: 1px solid #7ad9f5;
    background: #eafcff;
    color: #2bc2e1;
  }

  &:not(:disabled):active {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
  }

  &:not(:disabled):focus-visible {
    border: 1px solid #7ad9f5;
    background: #c9f4ff;
    color: #2bc2e1;
    box-shadow: 0 0 0 2px rgba(43, 194, 225, 0.2);
    outline: none;
  }
`;


const EmptyText = styled.div`
  color: #bcbcbc;
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  white-space: pre-line;
`;

const BottomInset = styled.div`
  height: ${convertToVwMob(90)};
`;
